import QuantityChangeText from 'components/atoms/quantity-change-text'
import Timeline from 'components/organisms/timeline-block'
import { MOVEMENT_ACTION } from 'domains/inventory/constants/inventory-constants'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { Button, Table } from 'reactstrap'
import TimeUtil from 'utils/time-util'

const propTypes = {
  inventoryMovements   : PropTypes.array,
  onProcessButtonClick : PropTypes.func,
  buttonLabel          : PropTypes.func
}

const dateFormat = 'MM/DD/YYYY HH:mm'

const getFirstPresent = (...values) => (
  values.find((value) => value !== undefined && value !== null && value !== '')
)

const getObjectName = (value) => {
  if (!value || typeof value !== 'object') return value

  return getFirstPresent(
    value.full_name,
    value.fullName,
    value.name,
    value.customer_name,
    value.customerName,
    value.buyer_name,
    value.buyerName,
    value.admin_name,
    value.adminName,
    value.email,
    value.code,
    value.number
  )
}

const normalizeText = (value) => {
  const normalizedValue = getObjectName(value)

  if (
    normalizedValue === undefined ||
    normalizedValue === null ||
    normalizedValue === ''
  ) return '-'

  return normalizedValue
}

const formatMovementNote = (value) => {
  const text = normalizeText(value)
  if (typeof text !== 'string' || text === '-') return text

  const reasonMatch = /(?:\.\s+|\n\s*)Reason:\s*/.exec(text)
  if (!reasonMatch) return text

  const action = text.slice(0, reasonMatch.index).trim()
  const reason = text.slice(reasonMatch.index + reasonMatch[0].length)
    .replace(/(?:\.\s+|\n\s*)Note:\s*/, '\nNote: ')

  return `${/^Action:/i.test(action) ? action : `Action: ${action}`}\nReason: ${reason}`
}

const humanizeAction = (value) => {
  if (!value) return '-'

  return String(value)
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const movementActionLabelMap = {
  [MOVEMENT_ACTION.CONSIGN]                   : 'Consign',
  [MOVEMENT_ACTION.PUT_AWAY]                  : 'Put Away',
  [MOVEMENT_ACTION.APPRAISAL]                 : 'Appraisal',
  [MOVEMENT_ACTION.FILL_APPRAISAL]            : 'Appraisal',
  [MOVEMENT_ACTION.CONFIRM_BUYING_PRICE]      : 'Konfirmasi Buying Price',
  [MOVEMENT_ACTION.BUYING_PRICE_CONFIRMATION] : 'Konfirmasi Buying Price',
  [MOVEMENT_ACTION.APPRAISAL_APPROVE]         : 'Appraisal Approve',
  [MOVEMENT_ACTION.APPROVE_APPRAISAL]         : 'Appraisal Approve',
  [MOVEMENT_ACTION.LIVE]                      : 'Live',
  [MOVEMENT_ACTION.CHECKOUT]                  : 'Checkout',
  [MOVEMENT_ACTION.ORDER]                     : 'Checkout',
  [MOVEMENT_ACTION.CANCEL]                    : 'Cancel',
  [MOVEMENT_ACTION.ORDER_EXPIRE]              : 'Cancel / Checkout Expired',
  [MOVEMENT_ACTION.AUTHENTICATION_CHECK]      : 'Authentication Check',
  [MOVEMENT_ACTION.INTERNAL_REQUEST_PICK]     : 'Internal Request Pick',
  [MOVEMENT_ACTION.INTERNAL_REQUEST_RETURNED] : 'Internal Request Returned',
  [MOVEMENT_ACTION.INTERNAL_REQUEST_PUT_AWAY] : 'Internal Request Put Away',
  [MOVEMENT_ACTION.REQUEST]                   : 'Internal Request',
  [MOVEMENT_ACTION.REQUEST_RETURN]            : 'Consignor Return',
  [MOVEMENT_ACTION.RETURN_TO_VENDOR]          : 'Consignor Return Ship',
  [MOVEMENT_ACTION.ORDER_SHIPMENT]            : 'Shipment',
  [MOVEMENT_ACTION.SHIPMENT]                  : 'Shipment',
  [MOVEMENT_ACTION.REFUND]                    : 'Refund',
  [MOVEMENT_ACTION.MOVE_IN]                   : 'Move In',
  [MOVEMENT_ACTION.MOVE_OUT]                  : 'Move Out',
  [MOVEMENT_ACTION.STOCK_OPNAME_CHECK_FOUND]  : 'STO Cek Fisik - Barang Ada',
  [MOVEMENT_ACTION.STOCK_OPNAME_CHECK_NOT_FOUND] : 'STO Cek Fisik - Barang Tidak Ada',
  [MOVEMENT_ACTION.MANUALLY_ADD_ITEM]         : 'Manual Add Item',
  [MOVEMENT_ACTION.MANUALLY_REMOVE_ITEM]      : 'Manual Remove Item',
  [MOVEMENT_ACTION.MANUAL_DELETE]             : 'Manual Delete',
  manual_make_sold                          : 'Make Sold (Manual SQ)',
  manual_make_in_stock                      : 'Make In Stock (Manual SQ)',
  [MOVEMENT_ACTION.MIGRATE_OLD_DATA]          : 'Migrate Old Data',
  [MOVEMENT_ACTION.SWAP_BKR_TO_BKX]           : 'Mark BKR as BKX',
  [MOVEMENT_ACTION.SWAP_BRV_TO_BKV]           : 'Swap BRV to BKV'
}

const getMovementActionLabel = (action) => (
  movementActionLabelMap[action] || humanizeAction(action)
)

const INTERNAL_REQUEST_ACTIONS = [
  MOVEMENT_ACTION.INTERNAL_REQUEST_PICK,
  MOVEMENT_ACTION.INTERNAL_REQUEST_RETURNED,
  MOVEMENT_ACTION.INTERNAL_REQUEST_PUT_AWAY,
  MOVEMENT_ACTION.REQUEST
].filter(Boolean)

const isInternalRequestMovement = (movement) => (
  INTERNAL_REQUEST_ACTIONS.includes(movement.action) ||
  String(movement.process_type || movement.processType || movement.reference_type || movement.referenceType || '').includes('InternalRequest')
)

const getMovementMetadata = (movement) => movement.metadata || {}

const getInternalRequestBorrowerLabel = (movement) => {
  const metadata = getMovementMetadata(movement)

  return normalizeText(getFirstPresent(
    movement.borrower_name,
    movement.borrowerName,
    movement.requestor_name,
    movement.requestorName,
    movement.requester_name,
    movement.requesterName,
    movement.internal_request_requestor_name,
    movement.internalRequestRequestorName,
    metadata.borrower_name,
    metadata.borrowerName,
    metadata.requestor_name,
    metadata.requestorName,
    metadata.requester_name,
    metadata.requesterName,
    metadata.internal_request_requestor_name,
    metadata.internalRequestRequestorName,
    getObjectName(movement.borrower),
    getObjectName(movement.requestor),
    getObjectName(movement.requester),
    getObjectName(metadata.borrower),
    getObjectName(metadata.requestor),
    getObjectName(metadata.requester),
    movement.borrower_email,
    movement.borrowerEmail,
    movement.requestor_email,
    movement.requestorEmail,
    movement.internal_request_requestor_email,
    metadata.borrower_email,
    metadata.borrowerEmail,
    metadata.requestor_email,
    metadata.requestorEmail,
    metadata.internal_request_requestor_email
  ))
}

const normalizeOrderNumber = (value) => {
  const text = normalizeText(value)

  if (text === '-') return '-'

  if (/^\d+$/.test(String(text))) return '-'

  return text
}

const getDealNumberFromDescription = (description) => {
  if (!description) return null

  const match = String(description).match(/Deal\s+([A-Z0-9-]+)/i)

  return match ? match[1] : null
}

const getMovementOrderNumber = (movement) => {
  const candidates = [
    movement.deal_code,
    movement.dealCode,
    movement.deal_number,
    movement.dealNumber,
    movement.order_code,
    movement.orderCode,
    movement.order_number,
    movement.orderNumber,
    getDealNumberFromDescription(movement.description),
    movement.process_code,
    movement.processCode,
    movement.process_number,
    movement.processNumber
  ]

  const validOrderNumber = candidates.find((candidate) => (
    normalizeOrderNumber(candidate) !== '-'
  ))

  return normalizeOrderNumber(validOrderNumber)
}

const getMovementCustomerName = (movement) => normalizeText(getFirstPresent(
  movement.customer_name,
  movement.customerName,
  movement.buyer_name,
  movement.buyerName,
  movement.customer_full_name,
  movement.customerFullName,
  movement.buyer_full_name,
  movement.buyerFullName,
  getObjectName(movement.customer),
  getObjectName(movement.buyer)
))

const getMovementReference = (movement) => {
  const borrowerLabel = getInternalRequestBorrowerLabel(movement)

  if (isInternalRequestMovement(movement) && borrowerLabel !== '-') return borrowerLabel

  return normalizeText(getFirstPresent(
    movement.reference_label,
    movement.referenceLabel,
    movement.reference_code,
    movement.referenceCode,
    movement.reference_number,
    movement.referenceNumber,
    getMovementOrderNumber(movement),
    movement.process_code,
    movement.processCode,
    movement.process_number,
    movement.processNumber,
    movement.process_id,
    movement.processId
  ))
}

const getMovementTitle = (movement) => {
  const actionLabel = getMovementActionLabel(movement.action)
  const reference   = getMovementReference(movement)

  return reference === '-' ? actionLabel : `${actionLabel} - ${reference}`
}

const getQuantityBefore = (afterValue, changeValue) => {
  const after = Number(afterValue)
  const change = Number(changeValue)

  if (Number.isNaN(after) || Number.isNaN(change)) return '-'

  return after - change
}

const getActorLabel = (movement) => normalizeText(getFirstPresent(
  movement.actor_name,
  movement.actorName,
  movement.actor_email,
  movement.actorEmail,
  movement.created_by_name,
  movement.createdByName,
  movement.admin_name,
  movement.adminName,
  movement.performed_by_name,
  movement.performedByName,
  getObjectName(movement.actor),
  getObjectName(movement.created_by),
  getObjectName(movement.createdBy),
  getObjectName(movement.admin),
  getObjectName(movement.performed_by),
  getObjectName(movement.performedBy)
))

const getTimestampLabel = (movement) => {
  const timestamp = getFirstPresent(movement.timestamp, movement.created_at, movement.createdAt)

  if (!timestamp) return '-'

  return TimeUtil.format(timestamp, dateFormat)
}

const buildDetailRows = (movement) => ([
  { label: 'Movement Type', value: getMovementActionLabel(movement.action), required: true },
  { label: 'Actor/User', value: getActorLabel(movement), required: true },
  { label: 'Customer/Buyer', value: getMovementCustomerName(movement) },
  {
    label: isInternalRequestMovement(movement) ? 'Requestor/User' : 'Reference Source',
    value: getMovementReference(movement)
  },
  {
    label: 'Location Before',
    value: normalizeText(getFirstPresent(
      movement.source_location_label,
      movement.sourceLocationLabel,
      movement.source_rack,
      movement.sourceRack,
      movement.source_sub_location,
      movement.sourceSubLocation
    ))
  },
  {
    label: 'Location After',
    value: normalizeText(getFirstPresent(
      movement.target_location_label,
      movement.targetLocationLabel,
      movement.target_rack,
      movement.targetRack,
      movement.target_sub_location,
      movement.targetSubLocation
    ))
  },
  { label: 'Reason / Note', value: formatMovementNote(getFirstPresent(movement.notes, movement.description, movement.metadata?.notes)) },
  { label: 'Timestamp', value: getTimestampLabel(movement), required: true }
].filter((row) => !row.hidden && (row.required || row.value !== '-')))

const buildCompactSummaryRows = (movement) => ([
  { label: 'Actor/User', value: getActorLabel(movement) },
  { label: 'Customer/Buyer', value: getMovementCustomerName(movement) }
].filter((row) => !row.hidden && (row.required || row.value !== '-')))

/**
 * Representing Inventory movement timeline in the entire system.
 * @param {array} inventoryMovements - collection of inventory movement
 * @param {func} onProcessButtonClick - handler props to redirect to the sub process of particular system
 * @param {func} buttonLabel - a function that will return the proper button label for current movement
 */
const InventoryMovementTimeline = ({
  inventoryMovements = [],
  onProcessButtonClick,
  buttonLabel
}) => {
  const [ openMovementIds, setOpenMovementIds ] = useState({})

  const actionColorMap = {
    [MOVEMENT_ACTION.CONSIGN]                   : 'success',
    [MOVEMENT_ACTION.PUT_AWAY]                  : 'success',
    [MOVEMENT_ACTION.APPRAISAL]                 : 'info',
    [MOVEMENT_ACTION.FILL_APPRAISAL]            : 'info',
    [MOVEMENT_ACTION.CONFIRM_BUYING_PRICE]      : 'info',
    [MOVEMENT_ACTION.BUYING_PRICE_CONFIRMATION] : 'info',
    [MOVEMENT_ACTION.APPRAISAL_APPROVE]         : 'success',
    [MOVEMENT_ACTION.APPROVE_APPRAISAL]         : 'success',
    [MOVEMENT_ACTION.LIVE]                      : 'success',
    [MOVEMENT_ACTION.AUTHENTICATION_CHECK]      : 'info',
    [MOVEMENT_ACTION.RETURN_TO_VENDOR]          : 'danger',
    [MOVEMENT_ACTION.REQUEST]                   : 'danger',
    [MOVEMENT_ACTION.REQUEST_RETURN]            : 'danger',
    [MOVEMENT_ACTION.INTERNAL_REQUEST_PICK]     : 'warning',
    [MOVEMENT_ACTION.INTERNAL_REQUEST_RETURNED] : 'warning',
    [MOVEMENT_ACTION.INTERNAL_REQUEST_PUT_AWAY] : 'success',
    [MOVEMENT_ACTION.ORDER]                     : 'warning',
    [MOVEMENT_ACTION.CHECKOUT]                  : 'warning',
    [MOVEMENT_ACTION.CANCEL]                    : 'warning',
    [MOVEMENT_ACTION.ORDER_EXPIRE]              : 'warning',
    [MOVEMENT_ACTION.ORDER_SHIPMENT]            : 'warning',
    [MOVEMENT_ACTION.SHIPMENT]                  : 'warning',
    [MOVEMENT_ACTION.REFUND]                    : 'info',
    [MOVEMENT_ACTION.MOVE_IN]                   : 'primary',
    [MOVEMENT_ACTION.MOVE_OUT]                  : 'primary',
    [MOVEMENT_ACTION.STOCK_OPNAME_CHECK_FOUND]  : 'success',
    [MOVEMENT_ACTION.STOCK_OPNAME_CHECK_NOT_FOUND] : 'danger',
    [MOVEMENT_ACTION.MANUALLY_ADD_ITEM]         : 'primary',
    [MOVEMENT_ACTION.MANUALLY_REMOVE_ITEM]      : 'primary',
    [MOVEMENT_ACTION.MANUAL_DELETE]             : 'danger',
    manual_make_sold                          : 'danger',
    manual_make_in_stock                      : 'success',
    [MOVEMENT_ACTION.MIGRATE_OLD_DATA]          : 'secondary',
    [MOVEMENT_ACTION.SWAP_BKR_TO_BKX]           : 'danger',
    [MOVEMENT_ACTION.SWAP_BRV_TO_BKV]           : 'primary'
  }

  const setTimelineType = (action) => actionColorMap[action] || 'default'

  // Reverse the timeline so user can see the latest movement on the top of the timeline.
  const movements = [ ...inventoryMovements ].reverse()

  const toggleMovementDetail = (movementKey) => {
    setOpenMovementIds((currentValue) => ({
      ...currentValue,
      [movementKey]: !currentValue[movementKey]
    }))
  }

  return (
    <div
      className="timeline timeline-one-side"
      data-timeline-axis-style="dashed"
      data-timeline-content="axis"
    >
      {
        movements.length === 0
          ? (
            <div className='d-flex justify-content-center'>
              { 'No timeline available' }
            </div>
          )
          : movements.map((movement, index) => {
            const movementKey      = movement.id || `${movement.action}-${movement.created_at}-${index}`
            const isDetailOpen     = !!openMovementIds[movementKey]
            const hasProcessButton = (movement.process_type || movement.process_detail_path || movement.detail_path) && onProcessButtonClick
            const detailRows       = buildDetailRows(movement)
            const summaryRows      = buildCompactSummaryRows(movement)

            return (
              <Timeline
                key={ movementKey }
                color={ setTimelineType(movement.action) }
                time={ movement.created_at }
                actionText={ getMovementActionLabel(movement.action) }
                title={ getMovementTitle(movement) }
              >
                {
                  summaryRows.length ? (
                    <div className='d-flex flex-wrap small text-muted mb-2'>
                      {
                        summaryRows.map((row) => (
                          <span key={ row.label } className='mr-3 mb-1'>
                            <strong>{ `${row.label}: ` }</strong>
                            { row.value }
                          </span>
                        ))
                      }
                    </div>
                  ) : null
                }

                { getFirstPresent(movement.notes, movement.description, movement.metadata?.notes) &&
                  <div className='small text-muted mb-2' style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{ formatMovementNote(getFirstPresent(movement.notes, movement.description, movement.metadata?.notes)) }</div> }

                <div className='mb-2'>
                  <Button
                    outline
                    size='sm'
                    color='primary'
                    onClick={ () => toggleMovementDetail(movementKey) }
                  >
                    { isDetailOpen ? 'Hide Detail' : 'Show Detail' }
                  </Button>
                </div>

                {
                  isDetailOpen ?
                    (
                      <>
                        <Table bordered size='sm' responsive className='mb-3'>
                          <tbody>
                            {
                              detailRows.map((row) => (
                                <tr key={ row.label }>
                                  <th scope='row' style={{ width: '35%' }}>{ row.label }</th>
                                  <td className={ row.label === 'Reason / Note' ? undefined : 'text-wrap' } style={ row.label === 'Reason / Note' ? { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' } : undefined }>{ row.value }</td>
                                </tr>
                              ))
                            }
                          </tbody>
                        </Table>

                        <Table bordered size='sm' responsive className='mb-0'>
                          <thead>
                            <tr>
                              <th>{ 'Perubahan Qty' }</th>
                              <th>{ 'Before' }</th>
                              <th>{ 'Change' }</th>
                              <th>{ 'After' }</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <th scope='row'>{ 'Sellable Qty (SQ)' }</th>
                              <td>{ normalizeText(getFirstPresent(movement.sellable_quantity_before, movement.sellableQuantityBefore, getQuantityBefore(movement.sellable_quantity, movement.sellable_quantity_change))) }</td>
                              <td>
                                <QuantityChangeText quantity={ movement.sellable_quantity_change } />
                              </td>
                              <td>{ normalizeText(movement.sellable_quantity) }</td>
                            </tr>
                            <tr>
                              <th scope='row'>{ 'Inventory Qty (IQ)' }</th>
                              <td>{ normalizeText(getFirstPresent(movement.stored_quantity_before, movement.storedQuantityBefore, getQuantityBefore(movement.stored_quantity, movement.stored_quantity_change))) }</td>
                              <td>
                                <QuantityChangeText quantity={ movement.stored_quantity_change } />
                              </td>
                              <td>{ normalizeText(movement.stored_quantity) }</td>
                            </tr>
                          </tbody>
                        </Table>

                        {
                          hasProcessButton ? (
                            <Button
                              outline
                              size='sm'
                              color='primary'
                              className="mt-2"
                              onClick={ () => onProcessButtonClick(movement) }
                            >
                              { buttonLabel ? buttonLabel(movement) : 'Open Process' }
                            </Button>
                          ) : null
                        }
                      </>
                    ) : null
                }
              </Timeline>
            )
          })
      }
    </div>
  )
}

InventoryMovementTimeline.propTypes = propTypes

export default InventoryMovementTimeline
